import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Button,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import RatePlanForm from "./RatePlanForm";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RatePlans = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const handleExportExcel = () => {
    const worksheetData = tableData.map((row) => ({
      ID: row.id,
      "Rate Plan ID": row.rate_plan_id,
      "Hotel ID": row.hotel_id,
      Name: row.name,
      "Rate Type": row.rate_type,
      Description: row.description,
      "Base Plan ID": row.base_plan_id,
      "Markup Type": row.markup_type,
      "Markup Value": row.markup_value,
      "Cancellation Policy ID": row.cancellation_policy_id,
      "Inclusion Codes": Array.isArray(row.inclusion_codes)
        ? row.inclusion_codes.join(", ")
        : row.inclusion_codes,
      "Min Stay": row.min_length_of_stay,
      "Max Stay": row.max_length_of_stay,
      Status: row.is_active ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RatePlans");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, "RatePlans.xlsx");
  };

  const columns = [
    { title: "ID", field: "id" },
    { title: "Rate Plan ID", field: "rate_plan_id" },
    { title: "Hotel ID", field: "hotel_id" },
    { title: "Name", field: "name" },
    { title: "Rate Type", field: "rate_type" },
    {
      title: "Description",
      field: "description",
      render: (rowData) => (
        <div
          style={{
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.description}
        </div>
      ),
    },
    { title: "Base Plan ID", field: "base_plan_id" },
    { title: "Markup Type", field: "markup_type" },
    { title: "Markup Value", field: "markup_value" },
    { title: "Cancellation Policy ID", field: "cancellation_policy_id" },
    {
      title: "Inclusion Codes",
      field: "inclusion_codes",
      render: (rowData) => (
        <div
          style={{
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {Array.isArray(rowData.inclusion_codes)
            ? rowData.inclusion_codes.join(", ")
            : rowData.inclusion_codes}
        </div>
      ),
    },
    { title: "Min Stay", field: "min_length_of_stay" },
    { title: "Max Stay", field: "max_length_of_stay" },
    {
      title: "Status",
      field: "is_active",
      render: (rowData) => (
        <Chip
          label={rowData.is_active ? "Active" : "Inactive"}
          color={rowData.is_active ? "success" : "error"}
          size="small"
        />
      ),
    },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/RatePlan");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editData?.id) {
        const payload = {
          data: {
            ...formData,
            rate_plan_id: parseInt(formData.rate_plan_id),
            hotel_id: formData.hotel_id ? parseInt(formData.hotel_id) : null,
            base_plan_id: formData.base_plan_id
              ? parseInt(formData.base_plan_id)
              : null,
            cancellation_policy_id: formData.cancellation_policy_id
              ? parseInt(formData.cancellation_policy_id)
              : null,
            markup_value: formData.markup_value
              ? parseFloat(formData.markup_value)
              : null,
            min_length_of_stay: formData.min_length_of_stay
              ? parseInt(formData.min_length_of_stay)
              : 1,
            max_length_of_stay: formData.max_length_of_stay
              ? parseInt(formData.max_length_of_stay)
              : null,
            inclusion_codes: Array.isArray(formData.inclusion_codes)
              ? formData.inclusion_codes
              : [],
          },
        };

        const response = await fetch(
          `https://api.esthell.com/RatePlan/${editData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update rate plan");
        }
        setNotification({
          open: true,
          message: "Rate plan updated successfully!",
          type: "success",
        });
      } else {
        // Generate rate_plan_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "RPI",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate rate plan ID");
        }

        const numberingData = await numberingResponse.json();
        const nextRatePlanId = parseInt(numberingData[0].last_gen_number) + 1;

        // Create rate plan with generated rate_plan_id
        const payload = {
          data: {
            ...formData,
            rate_plan_id: nextRatePlanId,
            hotel_id: formData.hotel_id ? parseInt(formData.hotel_id) : null,
            base_plan_id: formData.base_plan_id
              ? parseInt(formData.base_plan_id)
              : null,
            cancellation_policy_id: formData.cancellation_policy_id
              ? parseInt(formData.cancellation_policy_id)
              : null,
            markup_value: formData.markup_value
              ? parseFloat(formData.markup_value)
              : null,
            min_length_of_stay: formData.min_length_of_stay
              ? parseInt(formData.min_length_of_stay)
              : 1,
            max_length_of_stay: formData.max_length_of_stay
              ? parseInt(formData.max_length_of_stay)
              : null,
            inclusion_codes: Array.isArray(formData.inclusion_codes)
              ? formData.inclusion_codes
              : [],
          },
        };

        const response = await fetch(
          "https://api.esthell.com/create/RatePlan",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create rate plan");
        }
        setNotification({
          open: true,
          message: "Rate plan created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/RatePlan/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete rate plan");
      }

      fetchData();
      setNotification({
        open: true,
        message: "Rate plan deleted successfully!",
        type: "success",
      });
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Rate Plans
        </Typography>
        <Button variant="contained" color="primary" onClick={handleExportExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <RatePlanForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            handleSubmit={handleFormSubmit}
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RatePlans;
